/** Canvas context menu (spec §6B) — right-click blank canvas space ->
 * "Add Node" -> category flyout -> node type. Root categories stay fixed;
 * children open in a right-side cascading submenu (no inline accordion). */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { getAuthMatrixRevision, subscribeAuthMatrix } from './authMatrixRuntime'
import { addNodeCatalogByCategory } from './canvasNodeCatalog'
import { getNodeCatalogRevision, subscribeNodeCatalog } from './nodeCatalogRuntime'
import { useNodeIconRevision } from './nodeRegistry'
import { useCanvasNodeRegistryI18n } from './nodeRegistryI18n'
import type { CanvasNodeCategory } from './types'

interface Props {
  x: number
  y: number
  onPick: (nodeType: string) => void
  onClose: () => void
}

const FLYOUT_GAP = 6
const VIEWPORT_PAD = 8

export default function CanvasContextMenu({ x, y, onPick, onClose }: Props) {
  const { t } = useTranslation('canvas')
  const { nodeLabel, categoryLabel } = useCanvasNodeRegistryI18n()
  const iconRev = useNodeIconRevision()
  const catalogRev = useSyncExternalStore(subscribeNodeCatalog, getNodeCatalogRevision, getNodeCatalogRevision)
  const authRev = useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)
  const groups = useMemo(() => addNodeCatalogByCategory(), [iconRev, catalogRev, authRev])
  const categories = useMemo(() => groups.map((g) => g.category), [groups])

  const [openCategory, setOpenCategory] = useState<CanvasNodeCategory | null>(null)
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const [flyoutFocusIndex, setFlyoutFocusIndex] = useState(-1)
  const [flyoutPlacement, setFlyoutPlacement] = useState<'right' | 'left'>('right')
  const [flyoutStyle, setFlyoutStyle] = useState<React.CSSProperties>({})
  const [flyoutMaxHeight, setFlyoutMaxHeight] = useState<number | undefined>(undefined)
  // Root "Add Node" shell — anchored to the raw right-click point (`x`/`y`,
  // already client/viewport coordinates, spec §G). Starts unflipped at
  // that point; `positionShell` (below) measures the real rendered box on
  // mount/resize and corrects it against the actual viewport, exactly the
  // way `positionFlyout` already does for the submenu. Without this, a
  // right-click near the bottom or right edge of the screen opens a shell
  // that runs off-viewport with no correction at all (spec §BUG 2).
  const [shellStyle, setShellStyle] = useState<React.CSSProperties>({ left: x, top: y })
  const [rootMaxHeight, setRootMaxHeight] = useState<number | undefined>(undefined)

  const shellRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const rootScrollRef = useRef<HTMLDivElement>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)
  const flyoutScrollRef = useRef<HTMLDivElement>(null)
  const categoryRefs = useRef<Array<HTMLButtonElement | null>>([])

  const openItems = useMemo(
    () => groups.find((g) => g.category === openCategory)?.items ?? [],
    [groups, openCategory],
  )

  const openCategoryAt = useCallback(
    (index: number) => {
      const cat = categories[index]
      if (!cat) return
      setActiveCategoryIndex(index)
      setOpenCategory(cat)
      setFlyoutFocusIndex(-1)
    },
    [categories],
  )

  const positionFlyout = useCallback(() => {
    const root = rootRef.current
    const flyout = flyoutRef.current
    const catBtn = categoryRefs.current[activeCategoryIndex]
    if (!root || !flyout || !catBtn || !openCategory) return

    const rootRect = root.getBoundingClientRect()
    const catRect = catBtn.getBoundingClientRect()
    const flyoutWidth = flyout.offsetWidth || 200
    const flyoutHeight = flyout.offsetHeight
    // The outer `.pcv-context-menu-flyout` box has its own 1px border on
    // every side (inherited from the shared `.pcv-context-menu` rule) that
    // sits OUTSIDE the inner `.pcv-context-menu-flyout-scroll` wrapper
    // `maxHeight` is applied to below. Capping just the inner wrapper's
    // content to e.g. 109px still lets the outer box render ~2px taller
    // once its border is added back — a real, observed off-by-a-few-px
    // viewport overflow at exactly the boundary. Measuring this "chrome"
    // (whatever's outside the scrollable area) dynamically, rather than
    // hardcoding the current border width, keeps this correct if the
    // border/other outer chrome ever changes.
    const flyoutScrollHeight = flyoutScrollRef.current?.offsetHeight ?? flyoutHeight
    const flyoutChrome = Math.max(0, flyoutHeight - flyoutScrollHeight)
    const vw = window.innerWidth
    const vh = window.innerHeight

    const openRight = rootRect.right + FLYOUT_GAP + flyoutWidth <= vw - VIEWPORT_PAD
    setFlyoutPlacement(openRight ? 'right' : 'left')

    // `top` anchors the flyout to the active category row by default (see
    // VISTOERA_CANVAS_POPOVER_AND_SELECT_POSITIONING_UI_FIX_V1). Only the
    // real viewport bottom edge should ever push it up from there — an
    // earlier clamp here compared against `rootRect.height` (the compact
    // root menu's own height, e.g. ~4 short rows) instead of the viewport,
    // so any flyout taller than the root menu itself (the common case,
    // since flyouts list many more items than there are root categories)
    // was silently clamped back to top:0, aligning it with the *first*
    // category row no matter which row was actually hovered.
    let top = catRect.top - rootRect.top
    const maxHeight = vh - VIEWPORT_PAD * 2 - flyoutChrome
    const clampedMaxHeight = Math.max(0, Math.min(maxHeight, vh - catRect.top - VIEWPORT_PAD - flyoutChrome))
    const clampedTotalHeight = clampedMaxHeight + flyoutChrome
    if (catRect.top + flyoutHeight > vh - VIEWPORT_PAD) {
      top = Math.max(0, vh - VIEWPORT_PAD - Math.min(flyoutHeight, clampedTotalHeight) - rootRect.top)
    }

    setFlyoutStyle({
      top,
      // NOTE: `maxHeight` intentionally lives on the *inner* scroll wrapper
      // (`.pcv-context-menu-flyout-scroll`), not here — see the CSS comment
      // on `.pcv-context-menu-flyout` for why the outer box must stay a
      // non-scrolling, non-clipping container (its `::before` pointer
      // corridor bridge would otherwise be clipped along with it).
      ...(openRight
        ? { left: rootRect.width + FLYOUT_GAP }
        : { right: rootRect.width + FLYOUT_GAP }),
    })
    setFlyoutMaxHeight(clampedMaxHeight)
  }, [activeCategoryIndex, openCategory])

  // Root shell viewport-aware placement (spec §PART E/F/G) — flip
  // up/shift horizontally against the real viewport, and cap the
  // category list's own height with internal scroll if the menu is ever
  // taller than the available viewport space. Uses plain
  // `getBoundingClientRect()` + `window.innerWidth/innerHeight` (client
  // coordinates), never Canvas world x/y, so it stays correct regardless
  // of Canvas zoom/pan (the menu itself is a `position: fixed` overlay,
  // never affected by the Canvas transform in the first place).
  const positionShell = useCallback(() => {
    const shell = shellRef.current
    if (!shell) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    // `.pcv-context-menu-root-scroll` (the only element `rootMaxHeight`
    // actually constrains) sits inside `.pcv-context-menu-root`, which
    // also renders the title row plus its own 1px border + 6px padding on
    // every side — chrome that lives OUTSIDE the scrollable area. Capping
    // just the scroll wrapper to the full `availableHeight` would still
    // let the shell's *total* rendered height exceed the viewport by
    // that chrome amount (the exact off-by-a-few-px overflow already
    // fixed for the flyout submenu above). Measuring it dynamically
    // (rather than hardcoding border+padding+title numbers) keeps this
    // correct if that markup/CSS ever changes.
    const rootScrollHeight = rootScrollRef.current?.offsetHeight ?? 0
    const rect = shell.getBoundingClientRect()
    const naturalHeight = rect.height
    const chrome = Math.max(0, naturalHeight - rootScrollHeight)
    const availableHeight = vh - VIEWPORT_PAD * 2
    const height = Math.min(naturalHeight, availableHeight)
    const width = rect.width

    let top = y
    if (y + height > vh - VIEWPORT_PAD) {
      const availableAbove = y - VIEWPORT_PAD
      const availableBelow = vh - VIEWPORT_PAD - y
      // Flip upward only when there's genuinely more room above than
      // below (spec §PLACEMENT CONTRACT rule 2) — otherwise clamp in
      // place so the menu's bottom edge respects the viewport padding.
      top = availableAbove > availableBelow ? Math.max(VIEWPORT_PAD, y - height) : Math.max(VIEWPORT_PAD, vh - VIEWPORT_PAD - height)
    }
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - VIEWPORT_PAD - height))

    let left = x
    if (left + width > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - VIEWPORT_PAD - width)
    }
    left = Math.max(VIEWPORT_PAD, left)

    setShellStyle({ left, top })
    setRootMaxHeight(naturalHeight > availableHeight ? Math.max(0, availableHeight - chrome) : undefined)
  }, [x, y])

  useLayoutEffect(() => {
    positionShell()
  }, [positionShell])

  useLayoutEffect(() => {
    if (!openCategory) return
    positionFlyout()
  }, [openCategory, activeCategoryIndex, openItems.length, positionFlyout])

  // Recompute on resize only (spec §PART I — no continuous rAF/measurement
  // loop). A right-click menu is anchored to a one-off click point, not a
  // moving Canvas element, so window resize is the only external event
  // that can invalidate its already-computed position.
  useEffect(() => {
    const handleResize = () => {
      positionShell()
      if (openCategory) positionFlyout()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [openCategory, positionFlyout, positionShell])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (shellRef.current && !shellRef.current.contains(e.target as Node)) onClose()
    }
    const escHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (flyoutFocusIndex >= 0 || openCategory) {
        setFlyoutFocusIndex(-1)
        setOpenCategory(null)
        categoryRefs.current[activeCategoryIndex]?.focus()
        e.preventDefault()
        return
      }
      onClose()
    }
    const keyHandler = (e: KeyboardEvent) => {
      if (!shellRef.current?.contains(document.activeElement)) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const next = (activeCategoryIndex + 1) % categories.length
        openCategoryAt(next)
        categoryRefs.current[next]?.focus()
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = (activeCategoryIndex - 1 + categories.length) % categories.length
        openCategoryAt(prev)
        categoryRefs.current[prev]?.focus()
        return
      }
      if (e.key === 'ArrowRight' && openCategory && flyoutFocusIndex < 0) {
        e.preventDefault()
        setFlyoutFocusIndex(0)
        return
      }
      if (e.key === 'ArrowLeft' && flyoutFocusIndex >= 0) {
        e.preventDefault()
        setFlyoutFocusIndex(-1)
        categoryRefs.current[activeCategoryIndex]?.focus()
        return
      }
      if (openCategory && flyoutFocusIndex >= 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setFlyoutFocusIndex((i) => Math.min(i + 1, openItems.length - 1))
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setFlyoutFocusIndex((i) => Math.max(i - 1, 0))
        }
      }
    }

    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', escHandler)
    document.addEventListener('keydown', keyHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', escHandler)
      document.removeEventListener('keydown', keyHandler)
    }
  }, [activeCategoryIndex, categories.length, flyoutFocusIndex, onClose, openCategory, openCategoryAt, openItems.length])

  useEffect(() => {
    if (flyoutFocusIndex < 0) return
    const btn = flyoutRef.current?.querySelectorAll<HTMLButtonElement>('.pcv-context-menu-item')[flyoutFocusIndex]
    btn?.focus()
  }, [flyoutFocusIndex])

  const onShellMouseLeave = (e: React.MouseEvent) => {
    const next = e.relatedTarget as Node | null
    if (next && shellRef.current?.contains(next)) return
    setOpenCategory(null)
    setFlyoutFocusIndex(-1)
  }

  return (
    <div
      className="pcv-context-menu-shell"
      style={shellStyle}
      ref={shellRef}
      onMouseLeave={onShellMouseLeave}
    >
      <div className="pcv-context-menu pcv-context-menu-root" ref={rootRef}>
        <div className="pcv-context-menu-title">{t('contextMenu.title')}</div>
        <div className="pcv-context-menu-root-scroll" ref={rootScrollRef} style={{ maxHeight: rootMaxHeight }}>
          {groups.map(({ category }, index) => {
            const isActive = openCategory === category
            return (
              <div key={category} className="pcv-context-menu-group">
                <button
                  ref={(el) => {
                    categoryRefs.current[index] = el
                  }}
                  type="button"
                  className={[
                    'pcv-context-menu-item',
                    'pcv-context-menu-category',
                    isActive ? 'pcv-context-menu-category--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-expanded={isActive}
                  aria-haspopup="menu"
                  onMouseEnter={() => openCategoryAt(index)}
                  onFocus={() => openCategoryAt(index)}
                  onClick={() => openCategoryAt(index)}
                >
                  {categoryLabel(category)}
                  <span className="pcv-context-menu-arrow" aria-hidden="true">›</span>
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {openCategory ? (
        <div
          className={[
            'pcv-context-menu',
            'pcv-context-menu-flyout',
            flyoutPlacement === 'left' ? 'pcv-context-menu-flyout--left' : 'pcv-context-menu-flyout--right',
          ].join(' ')}
          ref={flyoutRef}
          style={flyoutStyle}
          role="menu"
          aria-label={categoryLabel(openCategory)}
          onMouseEnter={() => setOpenCategory(openCategory)}
        >
          <div className="pcv-context-menu-flyout-scroll" ref={flyoutScrollRef} style={{ maxHeight: flyoutMaxHeight }}>
            {openItems.map((def, itemIndex) => (
              <button
                key={def.nodeType}
                type="button"
                role="menuitem"
                className={[
                  'pcv-context-menu-item',
                  flyoutFocusIndex === itemIndex ? 'pcv-context-menu-item--focused' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                tabIndex={flyoutFocusIndex === itemIndex ? 0 : -1}
                onMouseEnter={() => setFlyoutFocusIndex(itemIndex)}
                onClick={() => onPick(def.nodeType)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onPick(def.nodeType)
                  }
                }}
              >
                <span className="pcv-context-menu-icon" style={{ color: def.color }}>
                  {def.icon}
                </span>
                {nodeLabel(def.nodeType, def.label)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
